import React, { useEffect } from 'react'
import { addDays, subDays } from 'date-fns'
import { AlertMessage, Container, Contents, LoadingContainer } from './styles'
import { SessionTitle } from '../SessionTitle'
import { IconComponent } from '~/assets/icons/component'
import { SpinnerIconLoader } from '~/components/shared'
import { useLaboratoryHealthProcedures, usePatient } from '~/context'
import { LaboratoryTestQuantitativeProperty } from '~/interfaces'
import { message } from '~/services'
import { getQuantitativePropertyReferenceValue, transformDate } from '~/utils'

export const BetaHCG: React.FC = () => {
  const { patient } = usePatient()
  const { selectedUrineCulture, betaHCG, betaHCGLoading, betaHCGError, fetchBetaHCGTest } =
    useLaboratoryHealthProcedures()

  useEffect(() => {
    if (selectedUrineCulture) {
      const { startDate } = selectedUrineCulture
      const TEST_CORRELATION_LOWER_BOUND = subDays(new Date(startDate), 270).toISOString()
      const TEST_CORRELATION_UPPER_BOUND = addDays(new Date(startDate), 5).toISOString()
      const BETA_HCG_TEST_CODES = [21563776, 25349654, 94408032]

      fetchBetaHCGTest({
        pathParams: { patientId: patient.id },
        queryParams: {
          testCodes: BETA_HCG_TEST_CODES,
          startDate: TEST_CORRELATION_LOWER_BOUND,
          endDate: TEST_CORRELATION_UPPER_BOUND
        }
      })
    }
  }, [fetchBetaHCGTest, patient.id, selectedUrineCulture])

  const eventDate = selectedUrineCulture ? transformDate({ date: selectedUrineCulture.startDate }) : 'Não informado'

  const renderLoading = () => {
    return (
      <LoadingContainer>
        <SpinnerIconLoader iconComponent={<IconComponent name={'loading'} />} size={75} velocity={2} />
      </LoadingContainer>
    )
  }

  const renderMessage = () => {
    return (
      <AlertMessage>
        <span>{message('laboratory.not_found_beta_hcg')}</span>
      </AlertMessage>
    )
  }

  const renderContent = (properties: LaboratoryTestQuantitativeProperty[]) => {
    return (
      <table>
        <tr className="header">
          <th>{message('laboratory.item')}</th>
          <th>{message('laboratory.result')}</th>
        </tr>
        {properties.map((data, index) => (
          <>
            <tr data-testid={'table rows'} key={index}>
              <td>{data.name || 'Não informado'}</td>
              <td>{data.value ? `${data.value} ${data.unit}` : 'Não informado'}</td>
            </tr>
            <hr />
            <div className="footerInfos">
              <span>
                <strong>Valor de Referência: </strong>
                {getQuantitativePropertyReferenceValue({
                  lowerValue: data.lowerReferenceValue,
                  upperValue: data.upperReferenceValue
                }) || 'Não informado'}
              </span>
              &#10072;
              <span>
                <strong>Data do Evento: </strong>
                {transformDate({ date: data.date }) || 'Não informado'}
              </span>
            </div>
          </>
        ))}
      </table>
    )
  }

  const render = () => {
    if (betaHCGError) return renderMessage()
    if (betaHCGLoading) return renderLoading()
    if (betaHCG) return renderContent(betaHCG as LaboratoryTestQuantitativeProperty[])
  }

  return (
    <Container>
      <SessionTitle title={message('laboratory.beta_hcg')} descriptionDate={betaHCG?.[0].date} />
      <Contents>{render()}</Contents>
    </Container>
  )
}
